 
/**
 * ParentShell.js — shared layout for ALL parent portal pages.
 * Provides:
 *   • Dark navy sidebar (logo card · Dashboard pill · Profile bar · 7 links · Privacy Shield · User pill)
 *   • Top Founder header (with "Message Robert Personally")
 *   • Orange "▼ HiBFF" privacy tab fixed at top of viewport
 *   • Bidirectional Welcome roll-up blind (only on dashboard route)
 *   • Founder Message modal
 *   • content slot for the page body
 *
 * Pages import { ParentShell } and pass their content as children.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Crown, Heart, BookOpen, HelpCircle, TrendingUp, Wallet,
  ShieldCheck, Mail, Send, ChevronDown, ChevronUp, ChevronRight,
  X, Check, Loader2, Menu, LayoutDashboard, Flag, LogOut,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';

// ── Privacy Shield panel content ──────────────────────────────────────
const SHIELD_PROTOCOLS = [
  { label: 'PII Redaction', body: "your teen's personal details never leave the device" },
  { label: 'Conversation Privacy', body: 'chat logs locked from this dashboard' },
  { label: 'Auto-Delete', body: 'sessions cleared on a strict rolling window' },
  { label: 'Safety Guardrails', body: '9 domains actively filtered in real time' },
];

// ── Sidebar links — every item is its own page ───────────────────────
export const NAV_LINKS = [
  { id: 'dashboard',  label: 'Dashboard',     icon: LayoutDashboard, route: '/parent-dashboard' },
  { id: 'home',       label: 'Home',          icon: Home,            route: '/parent-dashboard' },
  { id: 'upgrade',    label: 'Upgrade Plan',  icon: Crown,      route: '/parent-upgrade' },
  { id: 'hearts',     label: 'Hearts Store',  icon: Heart,      route: '/parent-hearts' },
  { id: 'challenges', label: 'Challenge Journey', icon: Flag,   route: '/parent-challenges' },
  { id: 'teen-vault', label: 'Teen Vault',    icon: Wallet,     route: '/parent-teen-vault' },
  { id: 'docs',       label: 'Docs',          icon: BookOpen,   route: '/parent-docs' },
  { id: 'help',       label: 'Help & Support',icon: HelpCircle, route: '/parent-help' },
  { id: 'progress',   label: 'Progress',      icon: TrendingUp, route: '/parent-progress' },
];

// =======================================================================
// Welcome Blind — bidirectional privacy curtain (only mounted on dashboard)
// =======================================================================
const WelcomeBlind = ({ firstName, open, onClose }) => (
  <div
    data-testid="parent-welcome-blind"
    aria-hidden={!open}
    className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center ${
      open ? 'translate-y-0' : '-translate-y-full'
    }`}
    style={{
      background: 'linear-gradient(180deg,#FDF8F2 0%,#FBF4EC 60%,#F7EFE4 100%)',
      pointerEvents: open ? 'auto' : 'none',
      transitionProperty: 'transform',
      transitionDuration: '3000ms',
      transitionTimingFunction: 'cubic-bezier(0.45, 0.05, 0.55, 0.95)',
      willChange: 'transform',
    }}
  >
    <div className="relative text-center px-4 sm:px-6 max-w-xl flex flex-col items-center w-full">
      <img src="/hibff-logo.png" alt="HiBFF" className="h-28 sm:h-40 w-auto max-w-full mb-6 select-none" draggable={false} />
      <p className="text-2xl sm:text-4xl text-amber-700/60 italic font-medium tracking-tight"
        style={{ fontFamily: '"Playfair Display","Georgia",serif' }}
        data-testid="parent-welcome-greeting">
        Welcome{firstName ? `, ${firstName}` : ''} <span aria-hidden>👋</span>
      </p>
      <h1 className="mt-3 text-2xl sm:text-4xl font-black text-neutral-900"
        style={{ fontFamily: '"Playfair Display","Georgia",serif' }}>
        Hi Brave Friends Forever
      </h1>
      <p className="mt-4 text-sm sm:text-lg text-indigo-500 italic max-w-md"
        style={{ fontFamily: '"Playfair Display","Georgia",serif' }}>
        Brave takes courage. Courage builds confidence. Confidence builds character.
      </p>
      <button
        onClick={onClose}
        data-testid="parent-welcome-enter-btn"
        className="mt-8 sm:mt-10 inline-flex items-center gap-2 px-8 sm:px-10 py-4 rounded-2xl bg-orange-500 hover:bg-orange-400 active:bg-orange-600 text-white font-bold text-base sm:text-lg shadow-xl hover:shadow-orange-400/40 transition-all"
      >
        Enter Dashboard
        <ChevronRight size={22} className="-rotate-90" />
      </button>
    </div>
  </div>
);

const PrivacyBlindTab = ({ onPull }) => (
  <button
    onClick={onPull}
    data-testid="privacy-blind-tab"
    aria-label="Pull privacy blind down"
    className="fixed left-1/2 -translate-x-1/2 top-0 z-[60] bg-orange-500 hover:bg-orange-400 active:bg-orange-600 text-white shadow-lg shadow-orange-500/30 hover:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 flex items-center gap-2 px-6 pt-2.5 pb-3.5 transition-all"
    style={{ borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}
  >
    <ChevronDown size={18} strokeWidth={2.5} />
    <span className="font-black tracking-wide text-sm" style={{ letterSpacing: '0.08em' }}>HiBFF</span>
  </button>
);

// =======================================================================
// Founder Message Modal
// =======================================================================
const FounderMessageModal = ({ open, onClose }) => {
  const [subject, setSubject] = useState('Direct message to Robert');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!open) return null;
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await apiClient.request('/api/parent/founder-message', {
        method: 'POST',
        body: JSON.stringify({ subject, body }),
      });
      setSent(true);
      setTimeout(() => { onClose(); setSent(false); setBody(''); }, 1400);
    } catch (e) {
      alert(e?.message || 'Could not send.');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[9000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="founder-message-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-testid="founder-message-close">
          <X size={20} />
        </button>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center text-white shadow">
            <Mail size={20} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Message Robert Personally</h3>
            <p className="text-xs text-gray-500">Lands directly on the Founder &amp; CEO&apos;s desk.</p>
          </div>
        </div>
        {sent ? (
          <div className="py-10 text-center" data-testid="founder-message-sent">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check className="text-emerald-600" />
            </div>
            <p className="font-semibold text-gray-900">Delivered. Robert will read it himself.</p>
          </div>
        ) : (
          <>
            <label className="block mt-5">
              <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Subject</span>
              <input value={subject} onChange={e => setSubject(e.target.value)}
                className="mt-1 w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none"
                data-testid="founder-message-subject" />
            </label>
            <label className="block mt-3">
              <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Your message</span>
              <textarea rows={6} value={body} onChange={e => setBody(e.target.value)}
                placeholder="Tell Robert what's on your mind. No filters, no gatekeeper."
                className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none resize-none"
                data-testid="founder-message-body" />
            </label>
            <button onClick={send} disabled={busy || !body.trim()}
              className="mt-5 w-full py-3 bg-gradient-to-r from-rose-600 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="founder-message-send">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />}
              Send to Robert
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// =======================================================================
// Sidebar parts
// =======================================================================
const PrivacyShieldPanel = () => (
  <div className="rounded-2xl border border-emerald-200/40 p-4 shadow-inner"
    style={{ background: 'linear-gradient(180deg,#F2FBF6 0%,#E9F6EF 100%)' }}
    data-testid="privacy-shield-panel">
    <div className="flex items-center gap-2 mb-3">
      <ShieldCheck className="text-emerald-600" size={18} />
      <h3 className="font-bold text-emerald-900 text-sm">HiBFF Privacy Shield</h3>
    </div>
    <ul className="space-y-2.5">
      {SHIELD_PROTOCOLS.map((p, i) => (
        <li key={i} className="text-[12px] text-neutral-800 flex items-start gap-2 leading-snug">
          <span className="text-emerald-500 mt-1 text-[10px]">●</span>
          <span><strong className="text-neutral-900">{p.label}</strong> <span className="text-neutral-600">— {p.body}</span></span>
        </li>
      ))}
    </ul>
  </div>
);

const SidebarUserPill = ({ firstName, fullName, tier }) => (
  <div className="mt-4 flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10" data-testid="sidebar-user-pill">
    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-400 via-violet-400 to-orange-400 flex items-center justify-center text-white font-black shadow-md ring-2 ring-white/20">
      {(firstName || 'P')[0].toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-white text-sm truncate">{fullName || firstName}</p>
      <p className="text-[10px] text-white/60 uppercase tracking-[0.18em] font-bold">{tier}</p>
    </div>
    <ChevronUp size={16} className="text-white/60" />
  </div>
);

const Sidebar = ({ activeId, firstName, fullName, tier, profilePct, isOpen, onClose }) => {
  const navigate = useNavigate();
  const handleNav = (route) => {
    navigate(route);
    if (onClose) onClose();
  };
  return (
    <>
      {/* Mobile backdrop — only renders below lg when drawer is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
          data-testid="parent-sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed lg:sticky left-0 top-0 z-50 flex flex-col w-72 h-screen px-5 py-6 overflow-y-auto transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'linear-gradient(180deg,#1E2A3A 0%,#1A2330 100%)' }}
        data-testid="parent-sidebar"
      >
        {/* Close button — mobile only */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-50"
          aria-label="Close menu"
          data-testid="parent-sidebar-close"
        >
          <X size={22} />
        </button>

      <div className="rounded-3xl bg-[#FDF8F2] shadow-xl p-6 mb-6 text-center">
        <img src="/hibff-logo.png" alt="HiBFF" className="h-20 w-auto mx-auto mb-3" draggable={false} />
        <h2 className="font-black text-neutral-900 text-base" style={{ fontFamily: '"Playfair Display","Georgia",serif' }}>
          Hi Brave Friends Forever
        </h2>
        <p className="mt-1 text-[11px] italic text-neutral-600 leading-snug" style={{ fontFamily: '"Playfair Display","Georgia",serif' }}>
          Brave takes courage. Courage builds confidence. Confidence builds character.
        </p>
      </div>

      {(() => {
        const dash = NAV_LINKS[0];
        const isActive = activeId === dash.id;
        return (
          <button
            onClick={() => handleNav(dash.route)}
            data-testid={`sidebar-nav-${dash.id}`}
            className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-2xl font-bold text-base mb-5 transition shadow-inner ${
              isActive ? 'bg-white/[0.08] text-amber-300 ring-1 ring-white/10' : 'bg-white/[0.04] text-white hover:bg-white/[0.07]'
            }`}
          >
            <Home size={20} className={isActive ? 'text-amber-300' : 'text-white/80'} />
            {dash.label}
          </button>
        );
      })()}

      <div className="px-1 mb-5">
        <div className="flex items-center justify-between text-[11px] text-white/60 mb-1.5">
          <span className="font-semibold uppercase tracking-widest">Profile</span>
          <span className="font-mono">{profilePct}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-400 to-purple-500 rounded-full transition-all duration-500"
            style={{ width: `${profilePct}%` }} />
        </div>
      </div>

      <nav className="flex-1 space-y-1">
        {NAV_LINKS.slice(1).map((link) => {
          const Icon = link.icon;
          const isActive = activeId === link.id;
          return (
            <button
              key={link.id}
              onClick={() => handleNav(link.route)}
              data-testid={`sidebar-nav-${link.id}`}
              className={`group relative w-full flex items-center gap-3 pl-5 pr-3 py-3 rounded-xl text-sm font-semibold transition ${
                isActive ? 'bg-white/[0.06] text-white' : 'text-white/85 hover:bg-white/[0.05] hover:text-white'
              }`}
            >
              <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1.5 rounded-r-md transition-all ${
                isActive ? 'bg-orange-500 h-8' : 'bg-orange-500/0 h-2 group-hover:bg-orange-500 group-hover:h-6'
              }`} />
              <Icon size={18} className="opacity-90" />
              <span>{link.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="mt-6">
        <PrivacyShieldPanel />
        <SidebarUserPill firstName={firstName} fullName={fullName} tier={tier} />
      </div>
    </aside>
    </>
  );
};

const FounderHeader = ({ firstName, onMessageRobert, title, onMenuClick, unread, onMessages, onSettings, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-3 sticky top-0 z-30" data-testid="parent-header">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label="Open menu"
          data-testid="parent-header-menu"
        >
          <Menu size={22} className="text-gray-700" />
        </button>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400 font-bold">Parent Dashboard</p>
          <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">{title || (firstName ? `Welcome back, ${firstName}` : 'Welcome back')}</h1>
        </div>
      </div>
      <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
        <button
          onClick={onMessages}
          className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="My Messages"
          title="My Messages"
          data-testid="parent-header-messages"
        >
          <Mail size={21} className="text-gray-700" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center shadow"
              data-testid="parent-header-messages-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
        <button
          onClick={onMessageRobert}
          className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-rose-600 to-orange-500 text-white font-bold text-sm shadow-lg hover:shadow-orange-500/40 hover:scale-[1.02] transition"
          data-testid="header-message-robert"
          aria-label="Message Robert Personally"
        >
          <Send size={16} />
          <span className="hidden sm:inline">Message Robert Personally</span>
        </button>
        <div className="relative">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-1 p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Account menu"
            data-testid="parent-account-menu-btn"
          >
            <span className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 via-violet-400 to-orange-400 flex items-center justify-center text-white font-black text-sm shadow ring-2 ring-white">
              {(firstName || 'P')[0].toUpperCase()}
            </span>
            <ChevronDown size={14} className={`text-gray-500 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div
                className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50"
                data-testid="parent-account-menu"
              >
                <button
                  onClick={() => { setMenuOpen(false); onSettings(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  data-testid="parent-menu-settings"
                >
                  <SettingsIcon size={16} className="text-gray-500" /> Account &amp; Settings
                </button>
                <p className="px-4 pt-0.5 pb-2 text-[11px] text-gray-400 leading-snug">
                  Manage subscription — pause, cancel or delete your account.
                </p>
                <div className="h-px bg-gray-100 my-1" />
                <button
                  onClick={() => { setMenuOpen(false); onLogout(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                  data-testid="parent-menu-logout"
                >
                  <LogOut size={16} /> Log out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

// =======================================================================
// ParentShell — the single layout used by every parent page
// =======================================================================
export const ParentShell = ({ activeId = 'dashboard', title, withBlind = false, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [blindOpen, setBlindOpen] = useState(withBlind);
  const [showFounder, setShowFounder] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  // Close the mobile drawer whenever route changes
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    apiClient.request('/api/help/unread-count')
      .then(d => setUnreadMsgs(d?.unread || 0))
      .catch(() => {});
  }, [location.pathname]);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await apiClient.request('/api/parent/custom-dashboard');
      setDashboard(res);
    } catch (e) {
      console.warn('[ParentShell] dashboard probe failed:', e);
      setDashboard({ parent: {}, teens: [] });
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard, location.pathname]);

  // Re-arm the welcome blind when navigating back to the dashboard
  useEffect(() => {
    if (withBlind) setBlindOpen(true);
  }, [withBlind]);

  const firstName = useMemo(() => {
    if (dashboard?.parent?.first_name && dashboard.parent.first_name !== 'Friend') return dashboard.parent.first_name;
    if (user?.name) return user.name.split(' ')[0];
    return '';
  }, [dashboard, user]);

  const fullName = dashboard?.parent?.name || user?.name || firstName;
  const rawTier = (user?.subscription_tier || 'free').toString().toLowerCase();
  const tierMap = { free: 'FIRST STEPS', starter: 'STEADY', plus_monthly: 'TOGETHER', plus_trial: 'TOGETHER', plus_annual: 'BRAVE', elite: 'TOGETHER', plus: 'TOGETHER', premium: 'BRAVE' };
  const tier = tierMap[rawTier] || `THE ${rawTier.toUpperCase()}`;

  const profilePct = useMemo(() => {
    const teens = dashboard?.teens?.length || 0;
    const aligned = (dashboard?.teens || []).filter(t => t.status_aligned).length;
    if (teens === 0) return 25;
    return Math.min(100, 50 + Math.round((aligned / teens) * 50));
  }, [dashboard]);

  return (
    <div
      className="min-h-screen flex bg-[#F8FAFC] lg:bg-transparent"
      style={{
        // Dark navy gutter behind the sticky sidebar — only painted at
        // ≥lg where the sidebar is permanent. Below lg the off-canvas
        // drawer overlays the page, so we keep the body cleanly light.
        backgroundImage:
          'linear-gradient(90deg, #1E2A3A 0 0, #1E2A3A 18rem, #F8FAFC 18rem)',
        backgroundAttachment: 'fixed',
        backgroundSize: 'auto',
      }}
      data-testid="parent-shell"
    >
      {/* The fixed gutter above only renders on ≥lg via a CSS trick:
          at <lg the sidebar is off-canvas so the gutter would be visible
          with no nav inside it. Hide it via a wrapper class. */}
      <style>{`
        @media (max-width: 1023px) {
          [data-testid="parent-shell"] { background-image: none !important; }
        }
      `}</style>

      <Sidebar
        activeId={activeId}
        firstName={firstName}
        fullName={fullName}
        tier={tier}
        profilePct={profilePct}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      <main className="flex-1 flex flex-col min-w-0">
        <FounderHeader
          firstName={firstName}
          title={title}
          onMessageRobert={() => setShowFounder(true)}
          onMenuClick={() => setMobileNavOpen(true)}
          unread={unreadMsgs}
          onMessages={() => navigate('/parent-help?messages=1')}
          onSettings={() => navigate('/settings')}
          onLogout={() => { logout(); navigate('/'); }}
        />
        <div className="flex-1">{children}</div>
      </main>

      <FounderMessageModal open={showFounder} onClose={() => setShowFounder(false)} />

      {withBlind && (
        <>
          <WelcomeBlind firstName={firstName} open={blindOpen} onClose={() => setBlindOpen(false)} />
          {!blindOpen && <PrivacyBlindTab onPull={() => setBlindOpen(true)} />}
        </>
      )}
    </div>
  );
};

export default ParentShell;
